import { Button, Icon, Input, Popover, Select, Text } from '@/ui'
import { faFilter, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { TransactionCategoryDropdown } from '@/components/TransactionCategoryDropdown'
import {
  RULE_USE_OPTIONS,
  RULE_TYPE_OPTIONS,
} from '@/constants/transactionRuleConstants'
import { FilterInputs, TransactionRuleScope } from './useTransactionRuleFilters'
import { UserSearchDropdown } from '@/components/UserSearchDropdown'

const SCOPE_OPTIONS: { value: TransactionRuleScope; text: string }[] = [
  { value: 'all', text: 'All rules' },
  { value: 'global', text: 'Global (applies to every user)' },
  { value: 'local', text: 'Local (applies to a specific user)' },
]

interface TransactionRuleFiltersProps {
  filterInputs: FilterInputs
  hasActiveFilters: boolean
  isLoading?: boolean
  onUpdateFilterInput: <K extends keyof FilterInputs>(
    key: K,
    value: FilterInputs[K]
  ) => void
  onApplyFilters: () => void
  onClearFilters: () => void
}

export const TransactionRuleFilters = ({
  filterInputs,
  hasActiveFilters,
  isLoading = false,
  onUpdateFilterInput,
  onApplyFilters,
  onClearFilters,
}: TransactionRuleFiltersProps) => {
  return (
    <div className="flex items-center gap-2">
      <Popover>
        <Popover.Trigger asChild>
          <Button
            variant={hasActiveFilters ? 'primary' : 'secondary'}
            size="sm"
            className="flex items-center gap-2"
          >
            <Icon
              icon={faFilter}
              size="sm"
              color={hasActiveFilters ? 'text-white' : 'text-neutral-600'}
            />
            Filters
            {hasActiveFilters && (
              <span className="ml-1 rounded-full bg-white/20 px-2 py-0.5 text-xs">
                Active
              </span>
            )}
          </Button>
        </Popover.Trigger>
        <Popover.Content align="start" header="Filter Transaction Rules">
          <div className="flex flex-col gap-4 w-[480px]">
            <div>
              <Text className="text-sm mb-1">Scope</Text>
              <Select
                value={filterInputs.scope}
                options={SCOPE_OPTIONS}
                onValueChange={(value) => onUpdateFilterInput('scope', value)}
              />
            </div>

            <div>
              <Text className="text-sm mb-1">Rule Use</Text>
              <Select
                value={filterInputs.ruleUse || 'all'}
                options={[
                  { value: 'all', text: 'All' },
                  ...RULE_USE_OPTIONS.map((opt) => ({
                    value: opt.value,
                    text: opt.text,
                  })),
                ]}
                onValueChange={(value) =>
                  onUpdateFilterInput(
                    'ruleUse',
                    value === 'all' ? '' : (value as string)
                  )
                }
              />
            </div>

            <div>
              <Text className="text-sm mb-1">Rule Text</Text>
              <Input
                value={filterInputs.ruleText}
                onChange={(e) =>
                  onUpdateFilterInput('ruleText', e.target.value)
                }
                placeholder="Search rule text..."
              />
            </div>

            <div>
              <Text className="text-sm mb-1">Rule Type</Text>
              <Select
                value={filterInputs.ruleType || 'all'}
                options={[
                  { value: 'all', text: 'All' },
                  ...RULE_TYPE_OPTIONS.map((opt) => ({
                    value: opt.value,
                    text: opt.text,
                  })),
                ]}
                onValueChange={(value) =>
                  onUpdateFilterInput(
                    'ruleType',
                    value === 'all' ? '' : (value as string)
                  )
                }
              />
            </div>

            <div>
              <Text className="text-sm mb-1">Category Type</Text>
              <Select
                value={filterInputs.transactionCategoryType || 'all'}
                options={[
                  { value: 'all', text: 'All' },
                  { value: 'business', text: 'Business' },
                  { value: 'personal', text: 'Personal' },
                ]}
                onValueChange={(value) =>
                  onUpdateFilterInput(
                    'transactionCategoryType',
                    value === 'all' ? '' : (value as string)
                  )
                }
              />
            </div>

            <div>
              <Text className="text-sm mb-1">Category</Text>
              <TransactionCategoryDropdown
                optionStyle="combineIdAndValue"
                value={
                  filterInputs.transactionCategoryId
                    ? Number(filterInputs.transactionCategoryId)
                    : undefined
                }
                onChange={(value) =>
                  onUpdateFilterInput(
                    'transactionCategoryId',
                    value ? String(value) : ''
                  )
                }
              />
            </div>

            {filterInputs.scope !== 'global' && (
              <div>
                <Text className="text-sm mb-1">User</Text>
                <UserSearchDropdown
                  value={
                    filterInputs.userId
                      ? Number(filterInputs.userId)
                      : undefined
                  }
                  onChange={(userId) =>
                    onUpdateFilterInput('userId', userId ? String(userId) : '')
                  }
                  placeholder="Search for user..."
                />
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Popover.Close asChild>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onApplyFilters}
                  disabled={isLoading}
                >
                  Apply Filters
                </Button>
              </Popover.Close>
              <Button
                variant="secondary"
                size="sm"
                onClick={onClearFilters}
                disabled={isLoading}
              >
                Clear
              </Button>
            </div>
          </div>
        </Popover.Content>
      </Popover>
      {hasActiveFilters && (
        <Button
          variant="secondary"
          size="sm"
          onClick={onClearFilters}
          className="flex items-center gap-1"
        >
          <Icon icon={faXmark} size="sm" color="text-neutral-600" />
          Clear Filters
        </Button>
      )}
    </div>
  )
}
